import React, { useState, useCallback } from 'react';
import {
  BarChart3, TrendingUp, CheckCircle, XCircle, Clock, ClipboardList,
  RefreshCw, AlertCircle, Users,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { claimsService, ClaimsReport } from '@/services/claimsService';
import { showToast } from '@/components/ui/Toast';

const COLORS = { approved: '#16a34a', declined: '#dc2626', pending: '#d97706' };

const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = new Date();
const thirtyDaysAgo = new Date(today);
thirtyDaysAgo.setDate(today.getDate() - 30);

const kpiCards = [
  { key: 'total',         label: 'Total Claims',  color: 'text-gray-900',   bg: 'bg-white',      border: 'border-l-gray-400',   icon: ClipboardList },
  { key: 'pending',       label: 'Pending',       color: 'text-yellow-700', bg: 'bg-yellow-50',  border: 'border-l-yellow-400', icon: Clock },
  { key: 'approved',      label: 'Approved',      color: 'text-green-700',  bg: 'bg-green-50',   border: 'border-l-green-500',  icon: CheckCircle },
  { key: 'declined',      label: 'Declined',      color: 'text-red-700',    bg: 'bg-red-50',     border: 'border-l-red-500',    icon: XCircle },
  { key: 'approval_rate', label: 'Approval Rate', color: 'text-indigo-600', bg: 'bg-indigo-50',  border: 'border-l-indigo-500', icon: TrendingUp },
] as const;

const SectionCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({
  title, icon, children,
}) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
      {icon}
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
    </div>
    <div className="overflow-hidden">{children}</div>
  </div>
);

const ClaimsReports: React.FC = () => {
  const [report, setReport] = useState<ClaimsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(toISO(thirtyDaysAgo));
  const [toDate, setToDate] = useState(toISO(today));

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await claimsService.getReports(fromDate, toDate);
      if (res.success) setReport(res.data);
    } catch {
      showToast('Failed to fetch report', 'error');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  return (
    <div className="flex flex-col h-full">
      {/* Fixed header with date filters */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 shrink-0">
          <BarChart3 className="h-5 w-5 text-brandColor" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Claims Reports</h1>
            <p className="text-xs text-gray-400">Analytics & agent performance</p>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Period:</span>
          <input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
          />
          <span className="text-gray-400 text-sm">–</span>
          <input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
          />
          <button
            onClick={fetchReport}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-brandColor text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60 whitespace-nowrap"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Apply
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="animate-spin h-8 w-8 border-2 border-brandColor border-t-transparent rounded-full" />
            </div>
          ) : !report ? (
            <div className="text-center py-32 text-gray-400">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Select a date range and click Apply</p>
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-5 gap-3">
                {kpiCards.map(c => {
                  const Icon = c.icon;
                  const val = c.key === 'approval_rate'
                    ? `${report.summary.approval_rate}%`
                    : report.summary[c.key as keyof typeof report.summary];
                  return (
                    <div
                      key={c.key}
                      className={`${c.bg} rounded-lg border border-gray-100 border-l-4 ${c.border} px-4 py-3 shadow-sm`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{c.label}</span>
                        <Icon className={`h-3.5 w-3.5 ${c.color}`} />
                      </div>
                      <p className={`text-2xl font-bold leading-none ${c.color}`}>{val}</p>
                    </div>
                  );
                })}
              </div>

              {/* Day Wise Trend */}
              <SectionCard title="Day Wise — Claims Over Time" icon={<TrendingUp className="h-4 w-4 text-gray-500" />}>
                {report.by_day.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No data for this period</p>
                ) : (
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={report.by_day} margin={{ top: 4, right: 20, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="day"
                          tick={{ fontSize: 11 }}
                          tickFormatter={v => {
                            const d = new Date(v + 'T00:00:00');
                            return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}`;
                          }}
                        />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip
                          labelFormatter={v => `Date: ${v}`}
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                        />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey="approved" stroke={COLORS.approved} strokeWidth={2} dot={{ r: 3 }} name="Approved" />
                        <Line type="monotone" dataKey="declined" stroke={COLORS.declined} strokeWidth={2} dot={{ r: 3 }} name="Declined" />
                        <Line type="monotone" dataKey="pending"  stroke={COLORS.pending}  strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 2" name="Pending" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </SectionCard>

              {/* Agent Wise + Approver Wise tables side by side */}
              <div className="grid grid-cols-2 gap-6">
                {/* Agent Breakdown */}
                <SectionCard title="By Agent" icon={<Users className="h-4 w-4 text-gray-500" />}>
                  {report.by_agent.length === 0 ? (
                    <div className="text-center py-10">
                      <AlertCircle className="h-7 w-7 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-400">No claims in this period</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b">
                          <th className="px-4 py-3 text-left">Agent</th>
                          <th className="px-4 py-3 text-center">Total</th>
                          <th className="px-4 py-3 text-center">Pending</th>
                          <th className="px-4 py-3 text-center">Approved</th>
                          <th className="px-4 py-3 text-center">Declined</th>
                          <th className="px-4 py-3 text-center">Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {report.by_agent.map(row => (
                          <tr key={row.agent_name} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-gray-900">{row.agent_name}</td>
                            <td className="px-4 py-3 text-center text-gray-700 font-medium">{row.total}</td>
                            <td className="px-4 py-3 text-center">
                              {row.pending > 0
                                ? <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">{row.pending}</span>
                                : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {row.approved > 0
                                ? <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">{row.approved}</span>
                                : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {row.declined > 0
                                ? <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">{row.declined}</span>
                                : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3 text-center text-xs font-semibold text-indigo-600">{row.approval_rate}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </SectionCard>

                {/* Approver Breakdown */}
                <SectionCard title="By Approver" icon={<CheckCircle className="h-4 w-4 text-gray-500" />}>
                  {report.by_approver.length === 0 ? (
                    <div className="text-center py-10">
                      <AlertCircle className="h-7 w-7 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-400">No actioned claims yet</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b">
                          <th className="px-4 py-3 text-left">Approver</th>
                          <th className="px-4 py-3 text-center">Total</th>
                          <th className="px-4 py-3 text-center">Approved</th>
                          <th className="px-4 py-3 text-center">Declined</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {report.by_approver.map(row => {
                          const total = row.approved + row.declined;
                          return (
                            <tr key={row.approver_name} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 font-medium text-gray-900">{row.approver_name}</td>
                              <td className="px-4 py-3 text-center text-gray-700 font-medium">{total}</td>
                              <td className="px-4 py-3 text-center">
                                {row.approved > 0
                                  ? <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">{row.approved}</span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {row.declined > 0
                                  ? <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">{row.declined}</span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </SectionCard>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClaimsReports;
